import type { BrokerOutreachCardDto, BrokerOutreachSentVia } from "@/api/brokerOutreach";
import type { BuyerSendVersionDto } from "@/api/buyerSendVersions";

/**
 * The ONE place a Buyer intro or Broker intro card's status is derived (J3).
 *
 * <p>The header pill, the default-open rule (D4), the collapse-and-advance after
 * an action (J6) and the Broker intro progress strip (D8) all call this module,
 * so the pill and the strip can never disagree about whether a card is done.</p>
 *
 * <p>🔴 <b>"Sent {date}" is the LATEST send (J2).</b> The forward-only columns
 * (`IntroEmailSentAtUtc ??=`, `BrokerIntroEmailSentAtUtc ??=`) hold the FIRST
 * send, so a pill read from them dates a re-sent email by its first send. The
 * outreach cards' `sentAtUtc` and the send versions are the latest send.</p>
 */

/** The outreach order (D4/D8) — email, text, web form, then the follow-ups. */
export const OUTREACH_ORDER = ["email", "text", "webForm", "followUpEmail", "followUpText"] as const;
export type OutreachCardKey = (typeof OUTREACH_ORDER)[number];

export type IntroCardHow = "from Phoenix" | "own email" | "combined";

export type IntroCardStatus =
  | { state: "notDrafted" }
  | { state: "draftSaved" }
  /** Copied to the clipboard, RECORD SEND / LOG not yet clicked — client-only. */
  | { state: "copied" }
  | { state: "sent"; date: string; how: IntroCardHow | null }
  | { state: "logged"; date: string };

/** Sent or logged — the strip's ✓ and the default-open rule's "done". */
export function isDone(status: IntroCardStatus): boolean {
  return status.state === "sent" || status.state === "logged";
}

/** The email cards say "recorded" (RECORD SEND); the text and web form say
 *  "logged" (LOG / I SUBMITTED IT). */
const LOGGED_CHANNELS = new Set<BrokerOutreachCardDto["channel"]>(["Text", "WebForm", "FollowUpText"]);

export function isLoggedChannel(channel: BrokerOutreachCardDto["channel"]): boolean {
  return LOGGED_CHANNELS.has(channel);
}

/**
 * A Broker intro card's status, from the server's card (D25) and the card's own
 * client-side "copied" flag. A done card stays done whatever the clipboard holds.
 */
export function outreachCardStatus(card: BrokerOutreachCardDto, copied = false): IntroCardStatus {
  if (card.status === "Sent" && card.sentAtUtc) {
    return isLoggedChannel(card.channel)
      ? { state: "logged", date: card.sentAtUtc }
      : { state: "sent", date: card.sentAtUtc, how: howOfVia(card.sentVia) };
  }
  if (copied) return { state: "copied" };
  return card.status === "DraftSaved" ? { state: "draftSaved" } : { state: "notDrafted" };
}

function howOfVia(via: BrokerOutreachSentVia | null): IntroCardHow | null {
  if (via === "OwnEmail") return "own email";
  if (via === "AppSend") return "from Phoenix";
  return null;
}

/** How a send version went: a combined intro, the consultant's own email, or a
 *  Phoenix send. `selfAttested === null` (carried in before versioning) is NOT
 *  RECORDED, never "from Phoenix". */
export function howOfVersion(v: Pick<BuyerSendVersionDto, "combinedIntroEmailId" | "selfAttested">): IntroCardHow | null {
  if (v.combinedIntroEmailId) return "combined";
  if (v.selfAttested === true) return "own email";
  if (v.selfAttested === false) return "from Phoenix";
  return null;
}

/** The newest version of a list (newest-first from the server, but chosen by
 *  date so the rule does not depend on that order). */
export function latestVersion(versions: readonly BuyerSendVersionDto[] | undefined): BuyerSendVersionDto | null {
  if (!versions || versions.length === 0) return null;
  return versions.reduce((a, b) => {
    const ta = Date.parse(a.sentAtUtc);
    const tb = Date.parse(b.sentAtUtc);
    return tb > ta || (tb === ta && b.versionNumber > a.versionNumber) ? b : a;
  });
}

/**
 * A message card read from its own DTO and its latest send version — the Buyer
 * intro card, and the broker email card when the outreach read failed (J6).
 *
 * <p>The latest version decides "sent" and its date. A recorded send with no
 * version (`sentAtUtc` set, history not loaded or carried in without one) still
 * reads as sent, dated by the column — the only date there is.</p>
 */
export function messageCardStatus(input: {
  generatedAtUtc: string | null;
  sentAtUtc: string | null;
  latest: BuyerSendVersionDto | null;
  copied?: boolean;
}): IntroCardStatus {
  if (input.latest) return { state: "sent", date: input.latest.sentAtUtc, how: howOfVersion(input.latest) };
  if (input.sentAtUtc) return { state: "sent", date: input.sentAtUtc, how: null };
  if (input.copied) return { state: "copied" };
  return input.generatedAtUtc ? { state: "draftSaved" } : { state: "notDrafted" };
}

/**
 * D4 + J6 — the card that starts open: the first card in outreach order that is
 * not done AND can be used (`available`). Null when there is none, so every card
 * starts collapsed.
 */
export function firstOpenCard<K extends string>(
  cards: readonly { key: K; status: IntroCardStatus; available: boolean }[],
): K | null {
  return cards.find((c) => c.available && !isDone(c.status))?.key ?? null;
}

/**
 * J6 — after an action in the tab folds a card, the card that opens next: the
 * first usable, not-done card AFTER it in outreach order, wrapping round to the
 * ones before it. Never the card just acted on.
 */
export function nextOpenCard<K extends string>(
  cards: readonly { key: K; status: IntroCardStatus; available: boolean }[],
  after: K,
): K | null {
  const i = cards.findIndex((c) => c.key === after);
  if (i < 0) return firstOpenCard(cards);
  return firstOpenCard([...cards.slice(i + 1), ...cards.slice(0, i)]);
}

/**
 * J8 — is the saved draft unsent work? True when it was generated or saved after
 * the latest send, so the editor shows with nothing in the way; false when the
 * draft is the one already sent, so it stays behind "Edit and send again".
 * ISO-8601 UTC strings compare by instant only when both carry the same shape,
 * so the comparison is on parsed times.
 */
export function draftNewerThanSend(draftTimes: readonly (string | null | undefined)[], sentAtUtc: string | null): boolean {
  if (!sentAtUtc) return true;
  const sent = Date.parse(sentAtUtc);
  return draftTimes.some((t) => t != null && Date.parse(t) > sent);
}
